/**
 * Environment detection from the hostname.
 *
 * PROD  = the bare product domains (responsekit.net / patrolschedule.com) and
 *         real tenant subdomains (e.g. pickerel.responsekit.net).
 * DEV   = dev.responsekit.net (and any dev.* / *.dev. / localhost).
 * UAT   = uat.* / staging.* / *.uat. / *.staging.*
 *
 * Used to render a non-prod environment badge so testers never mistake DEV/UAT
 * for PROD. PROD returns null (no badge).
 */
export type Environment = 'PROD' | 'DEV' | 'UAT';

export function getEnvironment(host: string = typeof window !== 'undefined' ? window.location.hostname : ''): Environment {
  const h = host.toLowerCase();

  if (h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local')) return 'DEV';
  if (h === 'dev.responsekit.net' || h.startsWith('dev.') || h.includes('.dev.')) return 'DEV';
  if (
    h.startsWith('uat.') || h.includes('.uat.') ||
    h.startsWith('staging.') || h.includes('.staging.') || h.includes('-staging.')
  ) return 'UAT';

  return 'PROD';
}

/** The badge label to overlay, or null in production (no badge). */
export function getEnvironmentBadge(host?: string): Exclude<Environment, 'PROD'> | null {
  const env = getEnvironment(host);
  return env === 'PROD' ? null : env;
}

/**
 * Full, human-readable environment name for the in-app badge.
 *
 * Deliberately separate from the short code: the favicon ribbon is drawn into
 * a 192px icon band where only "DEV"/"UAT" fits, while the in-app pill has
 * room for the full name. Returns null in production (no badge).
 */
export function getEnvironmentLabel(host?: string): string | null {
  const env = getEnvironmentBadge(host);
  if (!env) return null;
  return ENV_FULL_NAMES[env];
}

/** Full environment names, for surfaces with room for them — currently the
 * login-page banner, which a tester reads once deliberately rather than
 * glancing past. Kept SEPARATE from ENV_LABELS because the corner pill and the
 * favicon ribbon need short codes; conflating the two is what made the pill
 * verbose in #851. */
export const ENV_FULL_NAMES: Record<Exclude<Environment, 'PROD'>, string> = {
  DEV: 'Development (DEV) Environment',
  UAT: 'User Acceptance Testing (UAT) Environment',
};

/** The in-app corner pill's text. SHORT CODES, deliberately.
 *
 * These were briefly expanded to full names ("Development (DEV) Environment")
 * in #851. That was the wrong target: the intent was a fuller environment name
 * on the LOGIN BANNER and a pill for UAT that had been missing — not a verbose
 * pill. The pill is a persistent corner overlay on every screen, so it has to
 * stay small enough to not compete with the UI it sits on top of.
 *
 * The favicon ribbon also renders from these and only fits ~3 characters. */
export const ENV_LABELS: Record<Exclude<Environment, 'PROD'>, string> = {
  DEV: 'DEV',
  UAT: 'UAT',
};

/**
 * Badge colours, kept distinct so DEV and UAT are never confused at a glance
 * (and neither resembles prod, which shows no badge at all). Matches the
 * Sentinel IdP banner palette so the two systems agree per environment.
 */
export const ENV_COLORS: Record<Exclude<Environment, 'PROD'>, string> = {
  DEV: '#dc2626', // red
  UAT: '#b45309', // amber
};
